/**
 * Summarizes panel query results for the dashboard chat context envelope.
 * Runs on the frontend before sending to the backend — reuses cached data
 * from React Query so no duplicate warehouse queries are needed.
 */
import type { DashboardPanel } from '../types';

export interface PanelDataSummary {
  panelId: string;
  title: string;
  type: 'chart' | 'table' | 'metric' | 'funnel';
  sql: string;
  chartType?: string;
  metricValue?: string;
  stats?: { column: string; min: number; max: number; mean: number; trend: 'rising' | 'falling' | 'stable' };
  firstRows?: Record<string, unknown>[];
  lastRows?: Record<string, unknown>[];
  totalRowCount?: number;
  funnelSteps?: { step: string; count: number; conversion: string }[];
  overallConversion?: string;
  rowCount?: number;
  columns?: string[];
  error?: string;
}

interface PanelQueryResult {
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number | null;
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

function detectTrend(values: number[]): 'rising' | 'falling' | 'stable' {
  if (values.length < 3) return 'stable';
  const half = Math.floor(values.length / 2);
  const firstHalfMean = values.slice(0, half).reduce((a, b) => a + b, 0) / half;
  const secondHalfMean = values.slice(half).reduce((a, b) => a + b, 0) / (values.length - half);
  const ratio = firstHalfMean === 0 ? 1 : secondHalfMean / firstHalfMean;
  if (ratio > 1.1) return 'rising';
  if (ratio < 0.9) return 'falling';
  return 'stable';
}

export function summarizePanelData(
  panel: DashboardPanel,
  data: PanelQueryResult | null,
  error: string | null,
): PanelDataSummary {
  const base: PanelDataSummary = {
    panelId: panel.id,
    title: panel.title,
    type: panel.type,
    // Owner-side dashboard chat always has SQL; the public projection (which
    // never reaches this code path) redacts it.
    sql: panel.sql ?? '',
  };

  if (error) return { ...base, error };
  if (!data || data.rows.length === 0) return { ...base, rowCount: 0, columns: data?.columns };

  base.rowCount = data.rows.length;
  base.columns = data.columns;

  switch (panel.type) {
    case 'metric': {
      // Extract display value from first row's first numeric column
      const row = data.rows[0]!;
      for (const col of data.columns) {
        const val = row[col];
        if (typeof val === 'number' || (typeof val === 'string' && !isNaN(parseFloat(val)))) {
          base.metricValue = String(val);
          break;
        }
      }
      return base;
    }

    case 'chart': {
      base.chartType = panel.chartType;
      // Compute stats for the primary numeric column
      const numericCol = data.columns
        .slice(1)
        .find((col) =>
          data.rows.some(
            (r) => typeof r[col] === 'number' || (typeof r[col] === 'string' && !isNaN(parseFloat(r[col]))),
          ),
        );
      if (numericCol) {
        const values = data.rows.map((r) => toNumber(r[numericCol]));
        base.stats = {
          column: numericCol,
          min: Math.round(Math.min(...values) * 100) / 100,
          max: Math.round(Math.max(...values) * 100) / 100,
          mean: Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100,
          trend: detectTrend(values),
        };
      }
      base.firstRows = data.rows.slice(0, 3);
      base.lastRows = data.rows.length > 3 ? data.rows.slice(-3) : undefined;
      return base;
    }

    case 'table': {
      base.firstRows = data.rows.slice(0, 5);
      base.totalRowCount = data.rowCount ?? data.rows.length;
      return base;
    }

    case 'funnel': {
      const stepCol = panel.funnelConfig?.stepColumn ?? data.columns[0] ?? 'step';
      const valueCol = panel.funnelConfig?.valueColumn ?? data.columns[1] ?? 'count';
      const firstCount = toNumber(data.rows[0]?.[valueCol]);

      base.funnelSteps = data.rows.map((row) => {
        const count = toNumber(row[valueCol]);
        return {
          step: String((row[stepCol] as string | number | null) ?? ''),
          count,
          conversion: firstCount > 0 ? `${Math.round((count / firstCount) * 100)}%` : '0%',
        };
      });

      if (data.rows.length >= 2) {
        const lastCount = toNumber(data.rows[data.rows.length - 1]?.[valueCol]);
        base.overallConversion = firstCount > 0 ? `${Math.round((lastCount / firstCount) * 100)}%` : '0%';
      }
      return base;
    }

    default:
      return base;
  }
}
